import { useEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton, Stack, Typography, useTheme } from '@mui/material';
import GifBoxOutlinedIcon from '@mui/icons-material/GifBoxOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import CoreQuillEditor from '../../shared/CoreQuillEditor';
import type { IdeaComment } from '../../../interfaces/IdeaComment';
import type { Participant } from '../../../interfaces/Participant';
import useBoardStore from '../../../store/boardStore';
import useUserStore from '../../../store/userStore';
import TenorGifDialog from '../../idea-input/TenorGifDialog';
import { linkifyHtml } from '../../../utils/linkifyHtml';
import { dotvotingApi } from '../../../services/dotvotingApi';

export interface CardCommentsProps {
  comments: IdeaComment[];
  participants: Participant[];
  ideaId: string;
}

const formatDate = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Unknown time';
  }
  return date
    .toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
    .replace(' AM', ' am')
    .replace(' PM', ' pm');
};

const hasCommentContent = (value: string) =>
  /<img\b[^>]*>/i.test(value) ||
  value
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim().length > 0;

interface UrlPreview {
  title: string;
  image?: string;
}

const commentPreviewCache = new Map<string, Promise<UrlPreview | null>>();

const getCachedUrlPreview = (boardId: string, url: string): Promise<UrlPreview | null> => {
  const cacheKey = `${boardId}::${url}`;
  const cachedPreview = commentPreviewCache.get(cacheKey);
  if (cachedPreview) {
    return cachedPreview;
  }

  const previewPromise = dotvotingApi
    .getUrlPreview(boardId, url)
    .then((preview) => {
      if (!preview?.title) {
        return null;
      }

      return {
        title: preview.title,
        image: preview.image,
      };
    })
    .catch(() => null);

  commentPreviewCache.set(cacheKey, previewPromise);
  return previewPromise;
};

const extractAnchorHrefs = (html: string): string[] => {
  if (!html || typeof DOMParser === 'undefined') {
    return [];
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const hrefs = Array.from(doc.querySelectorAll('a[href]'))
    .map((anchor) => anchor.getAttribute('href')?.trim() ?? '')
    .filter(Boolean);

  return Array.from(new Set(hrefs));
};

const replaceLinksWithPreviews = (
  html: string,
  previewsByUrl: Record<string, UrlPreview | undefined>
): string => {
  if (!html || typeof DOMParser === 'undefined') {
    return html;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const anchors = Array.from(doc.querySelectorAll('a[href]'));

  anchors.forEach((anchor) => {
    const href = anchor.getAttribute('href')?.trim() ?? '';
    if (!href) {
      return;
    }

    const preview = previewsByUrl[href];
    if (!preview?.title) {
      return;
    }

    const previewLink = doc.createElement('a');
    previewLink.href = href;
    previewLink.target = '_blank';
    previewLink.rel = 'noopener noreferrer';
    previewLink.className = 'comment-link-preview';

    if (preview.image) {
      const previewImage = doc.createElement('img');
      previewImage.src = preview.image;
      previewImage.alt = preview.title;
      previewImage.loading = 'lazy';
      previewImage.className = 'comment-link-preview__image';
      previewLink.append(previewImage);
    }

    const previewTitle = doc.createElement('span');
    previewTitle.className = 'comment-link-preview__title';
    previewTitle.textContent = preview.title;

    previewLink.append(previewTitle);
    anchor.replaceWith(previewLink);
  });

  return doc.body.innerHTML;
};

const CardComments = ({ comments, participants, ideaId }: CardCommentsProps) => {
  const theme = useTheme();
  const addComment = useBoardStore((state) => state.addComment);
  const removeComment = useBoardStore((state) => state.removeComment);
  const boardDetails = useBoardStore((state) => state.boardDetails);
  const currentUser = useUserStore((state) => state.getCurrentUser());
  const [editorValue, setEditorValue] = useState('');
  const [isGifDialogOpen, setIsGifDialogOpen] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);
  const [previewsByUrl, setPreviewsByUrl] = useState<Record<string, UrlPreview | undefined>>({});
  const canComment = Boolean(currentUser) && !boardDetails?.locked;
  const giphyEnabled = boardDetails?.giphyEnabled !== false;
  const boardId = boardDetails?.id ?? boardDetails?._id ?? '';

  const canSubmitComment = canComment && hasCommentContent(editorValue);
  const linkifiedComments = useMemo(
    () =>
      comments.map((comment) => ({
        id: comment.id,
        content: linkifyHtml(comment.content),
      })),
    [comments]
  );
  const linkifiedCommentsById = useMemo(
    () =>
      Object.fromEntries(
        linkifiedComments.map((comment) => [comment.id, comment.content])
      ) as Record<string, string>,
    [linkifiedComments]
  );
  const linkedUrls = useMemo(
    () =>
      Array.from(
        new Set(linkifiedComments.flatMap((comment) => extractAnchorHrefs(comment.content)))
      ),
    [linkifiedComments]
  );

  const openGifDialog = () => {
    if (!giphyEnabled) {
      return;
    }

    setIsGifDialogOpen((previousValue) => !previousValue);
  };

  const closeGifDialog = () => {
    setIsGifDialogOpen(false);
  };

  const addGifComment = async (gifUrl: string) => {
    if (!currentUser || !giphyEnabled) {
      return;
    }

    await addComment(ideaId, `<div><img src="${gifUrl}" alt="GIF comment" /></div>`);
  };

  const submitComment = async () => {
    if (!canSubmitComment || !currentUser) {
      return;
    }

    await addComment(ideaId, editorValue);
    setEditorValue('');
  };

  const deleteCommentById = async (commentId: string) => {
    if (!currentUser || deletingCommentId) {
      return;
    }

    setDeletingCommentId(commentId);
    try {
      await removeComment(ideaId, commentId);
    } finally {
      setDeletingCommentId(null);
    }
  };

  useEffect(() => {
    if (!giphyEnabled) {
      setIsGifDialogOpen(false);
    }
  }, [giphyEnabled]);

  useEffect(() => {
    if (!boardId || linkedUrls.length === 0) {
      setPreviewsByUrl({});
      return;
    }

    let isCancelled = false;

    void Promise.all(
      linkedUrls.map(async (url) => {
        const preview = await getCachedUrlPreview(boardId, url);
        return { url, preview };
      })
    ).then((results) => {
      if (isCancelled) {
        return;
      }

      const nextState: Record<string, UrlPreview | undefined> = {};
      results.forEach(({ url, preview }) => {
        if (preview?.title) {
          nextState[url] = preview;
        }
      });
      setPreviewsByUrl(nextState);
    });

    return () => {
      isCancelled = true;
    };
  }, [boardId, linkedUrls]);

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          height: '100%',
          p: 2,
          pb: canComment ? '220px' : 2,
        }}
      >
        {comments.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center', mt: 2, fontWeight: 700 }}
          >
            No comments so far
          </Typography>
        )}
        {comments.map((comment) => {
          const commenter =
            participants.find((participant) => participant.id === comment.ownerId) ?? null;
          const isOwner = currentUser ? comment.ownerId === currentUser.id : false;
          const renderedCommentContent = replaceLinksWithPreviews(
            linkifiedCommentsById[comment.id] ?? linkifyHtml(comment.content),
            previewsByUrl
          );

          return (
            <Box
              key={comment.id}
              sx={{
                position: 'relative',
                borderRadius: 2,
                py: 1.5,
                px: 2,
                backgroundColor: '#F0F2F5',
                '&:hover .comment-delete-action, &:focus-within .comment-delete-action': {
                  opacity: 1,
                  pointerEvents: 'auto',
                },
              }}
            >
              <Typography
                variant="body2"
                component="div"
                sx={{
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                  '& img': { maxWidth: '100%' },
                  '& p, & div': {
                    margin: 0,
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                  },
                  '& a': {
                    color: 'primary.main',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                  },
                  '& .comment-link-preview': {
                    display: 'block',
                    textDecoration: 'none',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    my: 0.5,
                  },
                  '& .comment-link-preview__image': {
                    display: 'block',
                    width: '100%',
                    maxHeight: 140,
                    objectFit: 'cover',
                  },
                  '& .comment-link-preview__title': {
                    display: 'block',
                    px: 1,
                    py: 0.75,
                    color: 'text.primary',
                    fontWeight: 600,
                    lineHeight: 1.3,
                  },
                }}
                dangerouslySetInnerHTML={{ __html: renderedCommentContent }}
              />
              {isOwner && (
                <IconButton
                  className="comment-delete-action"
                  size="small"
                  aria-label="Delete comment"
                  disabled={Boolean(deletingCommentId)}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    opacity: 0,
                    pointerEvents: 'none',
                    transition: 'opacity 120ms ease-in-out',
                    '&:focus-visible': {
                      opacity: 1,
                      pointerEvents: 'auto',
                    },
                  }}
                  onClick={() => {
                    void deleteCommentById(comment.id);
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              )}
              <Stack direction="row" alignItems="center" justifyContent="space-between" mt={2}>
                <Typography variant="caption" color="#333" fontWeight="bold">
                  {commenter ? commenter.name : comment.ownerId}
                </Typography>
                <Typography variant="caption" color="#333" fontWeight="bold">
                  {formatDate(comment.added)}
                </Typography>
              </Stack>
            </Box>
          );
        })}
      </Box>
      {canComment && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 12,
            left: 12,
            right: 12,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            backgroundColor: theme.palette.background.paper,
            p: 1,
          }}
        >
          {!isGifDialogOpen ? (
            <>
              <Box
                sx={{
                  '.ql-container': {
                    border: 0,
                  },
                  '.ql-editor': {
                    height: 80,
                    padding: theme.spacing(1),
                  },
                }}
              >
                <CoreQuillEditor value={editorValue} onChange={setEditorValue} />
              </Box>
              <Stack direction="row" justifyContent="space-between" mt={1}>
                {giphyEnabled ? (
                  <IconButton
                    size="small"
                    onClick={openGifDialog}
                    aria-label="Add GIF from Tenor"
                    color="primary"
                  >
                    <GifBoxOutlinedIcon />
                  </IconButton>
                ) : (
                  <Box />
                )}
                <Button
                  variant="contained"
                  size="small"
                  disabled={!canSubmitComment}
                  onClick={submitComment}
                >
                  Post comment
                </Button>
              </Stack>
            </>
          ) : (
            <TenorGifDialog onClose={closeGifDialog} onSelectGif={addGifComment} />
          )}
        </Box>
      )}
    </>
  );
};

export default CardComments;
